import { ServiceActivityResponseDataType } from '../@types/service.type';

/**
 * Accessible name of the activity matrix table.
 *
 * `e2e/pages/HomePage.ts` locates the dashboard with
 * `getByRole('table', { name: 'Service Activity Monitor' })`. Keep this a named
 * constant so the string can't be quietly renamed out from under the e2e smoke
 * test, and assert it in the matrix unit tests so a break fails fast in Jest.
 */
export const ACTIVITY_TABLE_ARIA_LABEL = 'Service Activity Monitor';

/**
 * The three semantic lanes the ten statuses fall into. The statuses are not a
 * flat list — they describe a job flow, a parts sub-flow that runs alongside
 * it, and an exception rail — and the rail renders that structure.
 */
export enum ActivityLane {
  JobFlow = 'jobFlow',
  Parts = 'parts',
  Exception = 'exception',
}

/** Numeric count fields on the activity-monitor payload. */
export type ActivityStatusField = Extract<
  keyof ServiceActivityResponseDataType,
  | 'newStatus'
  | 'bookingStatus'
  | 'fieldReadyStatus'
  | 'otwStatus'
  | 'onsiteStatus'
  | 'partsRequiredStatus'
  | 'partsProcessedStatus'
  | 'logisticsStatus'
  | 'dispatchedStatus'
  | 'escalatedStatus'
>;

export interface ActivityStatusColumn {
  field: ActivityStatusField;
  /** Column header, and the label shown in the flow rail. */
  header: string;
  /**
   * Status-group key handed to the job list as `?serviceStatusGroup=`.
   *
   * MUST be byte-identical to a `case` label in
   * `getServiceStatusNamesByStatusGroup` (`src/utils/services.ts`). A typo
   * expands to `''`, which silently drills through to *every* status instead
   * of erroring — `service-activity.constant.tests.ts` guards the coupling.
   */
  group: string;
  lane: ActivityLane;
  /**
   * Columns that mean "someone needs to act". Carried through to the cell
   * heat ramp so these read warm while the rest read cool — the same
   * semantic the old blanket-pink highlighting carried, but proportional.
   */
  alert?: boolean;
}

/** The ten status columns, in workflow order. Single source of truth for both
 *  the flow rail and the matrix. */
export const ACTIVITY_STATUS_COLUMNS: readonly ActivityStatusColumn[] = [
  { field: 'newStatus', header: 'New', group: 'New', lane: ActivityLane.JobFlow, alert: true },
  { field: 'bookingStatus', header: 'Booking', group: 'Booking', lane: ActivityLane.JobFlow },
  {
    field: 'fieldReadyStatus',
    header: 'Field Ready',
    group: 'Field Ready',
    lane: ActivityLane.JobFlow,
  },
  { field: 'otwStatus', header: 'OTW', group: 'OTW', lane: ActivityLane.JobFlow },
  { field: 'onsiteStatus', header: 'On Site', group: 'On Site', lane: ActivityLane.JobFlow },
  {
    field: 'partsRequiredStatus',
    header: 'Parts Required',
    group: 'Parts Required',
    lane: ActivityLane.Parts,
    alert: true,
  },
  {
    field: 'partsProcessedStatus',
    header: 'Parts (Processed)',
    group: 'Parts (Processed)',
    lane: ActivityLane.Parts,
  },
  { field: 'logisticsStatus', header: 'Logistics', group: 'Logistics', lane: ActivityLane.Parts },
  {
    field: 'dispatchedStatus',
    header: 'Dispatched',
    group: 'Dispatched',
    lane: ActivityLane.Parts,
  },
  {
    field: 'escalatedStatus',
    header: 'Escalated',
    group: 'Escalated',
    lane: ActivityLane.Exception,
    alert: true,
  },
];

export interface ActivityLaneMeta {
  lane: ActivityLane;
  label: string;
  /**
   * Lane whose largest stage is the denominator for this lane's magnitude
   * bars. Exception scales against JobFlow: a single-stage lane measured
   * against itself would always render a full bar, which would read as
   * "everything is escalated" no matter the number.
   */
  scaleAgainst: ActivityLane;
}

export const ACTIVITY_LANES: readonly ActivityLaneMeta[] = [
  { lane: ActivityLane.JobFlow, label: 'Job flow', scaleAgainst: ActivityLane.JobFlow },
  { lane: ActivityLane.Parts, label: 'Parts', scaleAgainst: ActivityLane.Parts },
  { lane: ActivityLane.Exception, label: 'Escalation', scaleAgainst: ActivityLane.JobFlow },
];

/** Sentinel group label for rows whose `clientCode` is blank. Grouped under an
 *  explicit bucket rather than dropped, so counts always reconcile. */
export const UNGROUPED_CLIENT_LABEL = 'Unassigned';

/**
 * Auto-refresh cadence, in seconds.
 *
 * Single source of truth: the query derives its `refetchInterval` from this and
 * the header renders it in the "Live · every 120s" label. Previously the label
 * and the interval were separate literals and could drift, telling the user one
 * cadence while polling at another.
 */
export const REFRESH_INTERVAL_SECONDS = 120;
export const REFRESH_INTERVAL_MS = REFRESH_INTERVAL_SECONDS * 1000;
